const AVAILABLE = "available";

export const availableEvidence = (data) => ({ state: AVAILABLE, data });
export const unavailableEvidence = (reason = "Evidence is unavailable.") => ({
  state: "unavailable",
  reason,
});

const isAvailable = (evidence) => evidence?.state === AVAILABLE;
const asArray = (value) => (Array.isArray(value) ? value : []);
const publicName = (value, fallback) => {
  const name = String(value || "").trim();
  return name || fallback;
};
const runtimeSnapshot = (runtime) => {
  if (runtime && typeof runtime === "object" && !Array.isArray(runtime)) return runtime;
  if (typeof runtime !== "string" || !runtime.trim()) return null;
  try {
    const parsed = JSON.parse(runtime);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch (_error) {
    return null;
  }
};
const controllerLabel = (controller) => {
  const name = publicName(controller?.name, "Unnamed application");
  const target = String(controller?.target?.name || "").trim();
  return target ? `${name} on ${target}` : name;
};

const route = (sectionId, label, search = "") => ({ sectionId, label, search });
const serverSection = (key) => ({
  modbus: "plc-protocol-services",
  opcua: "opcua-server",
  s7comm: "s7comm-server",
}[key] || "plc-protocol-services");

const forceCount = (controller) => {
  const candidates = [
    controller?.active_force_count,
    controller?.force_count,
    controller?.actual?.active_force_count,
  ];
  const value = candidates.find((candidate) => Number.isInteger(candidate) && candidate >= 0);
  return Number.isInteger(value) ? value : null;
};

const controllerDrifted = (controller) => {
  const actual = controller?.actual;
  if (!actual?.authenticated || actual.stale) return false;
  return (
    (actual.state && controller.desired_state && actual.state !== controller.desired_state) ||
    (Number.isInteger(actual.revision) && actual.revision !== controller.desired_revision) ||
    (Number.isInteger(actual.generation) && actual.generation !== controller.desired_generation)
  );
};

const protocolCondition = (key, name, status) => {
  if (!status || status.available === false) {
    return { availability: "Unavailable", issue: null };
  }
  const lifecycle = String(status.lifecycle || status.runtime?.lifecycle || "").toLowerCase();
  const explicitError = String(
    status.last_error || status.error || status.runtime?.last_error || ""
  ).trim();
  if (explicitError || ["faulted", "failed", "error", "degraded"].includes(lifecycle)) {
    return {
      availability: lifecycle === "degraded" ? "Degraded" : "Needs attention",
      issue: {
        key: `protocol:${name}`,
        severity: lifecycle === "faulted" || lifecycle === "failed" ? "critical" : "warning",
        title: `${name} server needs attention`,
        detail: "This server reported a problem. Open its settings to review it.",
        link: route(serverSection(key), `Open ${name}`),
      },
    };
  }
  if (status.executable === false || status.mode === "configuration_only") {
    return { availability: "Configuration only", issue: null };
  }
  if (status.running === true || status.listening === true || status.runtime?.listening === true) {
    return { availability: "Listening", issue: null };
  }
  if (status.enabled === false || status.runtime?.enabled === false) {
    return { availability: "Disabled", issue: null };
  }
  return { availability: "Evidence loaded", issue: null };
};

export function buildPLCOverview(evidence = {}) {
  const issues = [];
  const coverage = [];

  const hostsAvailable = isAvailable(evidence.runtimeHosts);
  const hosts = hostsAvailable ? asArray(evidence.runtimeHosts.data) : [];
  const offlineHosts = hosts.filter((host) => host?.status !== "online");
  if (hostsAvailable) {
    offlineHosts.forEach((host) => {
      const neverConnected = !host?.last_seen_at;
      issues.push({
        key: `host:${host?.id || host?.name || issues.length}`,
        severity: neverConnected ? "info" : "warning",
        title: `${publicName(host?.name, "Unnamed runtime host")} is ${neverConnected ? "not connected" : "offline"}`,
        detail: neverConnected
          ? "This runtime has not connected yet."
          : "This runtime is not currently connected.",
        link: route("plc-runtime-fleet", "Open runtimes", "?view=hosts"),
      });
    });
    coverage.push({ key: "hosts", label: "Runtimes", state: "Available", detail: `Status loaded for ${hosts.length} runtime${hosts.length === 1 ? "" : "s"}.` });
  } else {
    coverage.push({ key: "hosts", label: "Runtimes", state: "Unavailable", detail: "Runtime status could not be loaded." });
  }

  let connectionHosts = 0;
  let totalConnections = 0;
  let runningConnections = 0;
  hosts.forEach((host) => {
    const connectors = runtimeSnapshot(host?.runtime)?.connectors;
    if (!Array.isArray(connectors)) return;
    connectionHosts += 1;
    totalConnections += connectors.length;
    if (host?.status === "online") {
      runningConnections += connectors.filter((connector) => connector?.actual_state === "running").length;
    }
  });
  const connectionSummary = hostsAvailable && (hosts.length === 0 || connectionHosts > 0)
    ? {
      running: runningConnections,
      total: totalConnections,
      complete: connectionHosts === hosts.length,
    }
    : null;

  const controllersAvailable = isAvailable(evidence.controllers);
  const controllerPayload = controllersAvailable ? evidence.controllers.data || {} : {};
  const controllers = controllersAvailable ? asArray(controllerPayload.controllers) : [];
  let forceEvidenceCount = 0;
  let activeForces = 0;
  controllers.forEach((controller) => {
    const name = controllerLabel(controller);
    const actual = controller?.actual || {};
    if (actual.state === "faulted" || actual.state === "fault") {
      issues.push({
        key: `controller-fault:${controller?.id || name}`,
        severity: "critical",
        title: `${name} is faulted`,
        detail: "The runtime reported an application fault.",
        link: route("plc-runtime-fleet", "Open runtimes"),
      });
    } else if (actual.stale && controller?.target?.available !== false && controller?.configuration_only !== true) {
      issues.push({
        key: `controller-stale:${controller?.id || name}`,
        severity: "warning",
        title: `${name} status is out of date`,
        detail: "This application's status has not been updated recently.",
        link: route("plc-runtime-fleet", "Open runtimes"),
      });
    }
    if (controllerDrifted(controller)) {
      issues.push({
        key: `controller-drift:${controller?.id || name}`,
        severity: "warning",
        title: `${name} differs from desired state`,
        detail: "Its reported state does not match the requested state.",
        link: route("plc-runtime-fleet", "Open runtimes"),
      });
    }
    const count = forceCount(controller);
    if (count != null) {
      forceEvidenceCount += 1;
      if (count > 0) {
        activeForces += count;
        issues.push({
          key: `controller-forces:${controller?.id || name}`,
          severity: "warning",
          title: `${name} reports ${count} active force${count === 1 ? "" : "s"}`,
          detail: "Active engineering overrides require persistent review and explicit clearing.",
          link: route("plc-runtime-fleet", "Open runtimes"),
        });
      }
    }
  });
  if (controllersAvailable) {
    coverage.push({ key: "controllers", label: "Applications", state: "Available", detail: `Status loaded for ${controllers.length} application${controllers.length === 1 ? "" : "s"}.` });
  } else {
    coverage.push({ key: "controllers", label: "Applications", state: "Unavailable", detail: "Application status could not be loaded." });
  }
  if (forceEvidenceCount > 0) {
    coverage.push({
      key: "forces",
      label: "Application overrides",
      state: forceEvidenceCount === controllers.length ? "Available" : "Partial",
      detail: activeForces > 0
        ? `${activeForces} active override${activeForces === 1 ? "" : "s"} reported.`
        : "No active overrides reported.",
    });
  }

  const runningControllers = controllers.filter((controller) => (
    controller?.actual?.authenticated && !controller.actual.stale && controller.actual.state === "running"
  )).length;

  const deploymentsAvailable = isAvailable(evidence.deployments);
  const deployments = deploymentsAvailable ? asArray(evidence.deployments.data) : [];
  deployments.forEach((deployment) => {
    const phase = String(deployment?.phase || "").toLowerCase();
    if (phase.includes("fail")) {
      issues.push({
        key: `deployment:${deployment?.id || deployment?.name || issues.length}`,
        severity: "critical",
        title: `${publicName(deployment?.name, "Unnamed deployment")} failed`,
        detail: "This deployment did not finish successfully. Open deployment history to review it.",
        link: route("plc-deployments", "Open deployments"),
      });
    }
  });
  coverage.push({
    key: "deployments",
    label: "Deployment activity",
    state: deploymentsAvailable ? "Available" : "Unavailable",
    detail: deploymentsAvailable
      ? deployments.length > 0 ? `${deployments.length} recent deployment${deployments.length === 1 ? "" : "s"}.` : "No recent fleet deployments."
      : "Deployment activity could not be loaded.",
  });

  const outputEvidence = evidence.outputs;
  const outputEntries = isAvailable(outputEvidence) ? asArray(outputEvidence.data?.entries) : [];
  if (runningControllers > 0) {
    outputEntries.forEach(({ controllerName, status }, index) => {
      if (status?.available !== true) return;
      const total = Number(status?.counts?.total) || 0;
      const accepted = Number(status?.counts?.accepted) || 0;
      const pending = Number(status?.counts?.pending) || 0;
      const leased = Number(status?.counts?.leased) || 0;
      if (pending > 0 || leased > 0 || accepted < total) {
        issues.push({
          key: `output:${controllerName || index}`,
          severity: "warning",
          title: `${publicName(controllerName, "Unnamed application")} has output changes waiting for confirmation`,
          detail: "Some requested output changes have not been confirmed yet.",
          link: route("plc-runtime-fleet", "Open runtimes"),
        });
      }
    });
  }
  const outputChecked = Number(outputEvidence?.data?.checked) || 0;
  const outputAttempted = Number(outputEvidence?.data?.attempted) || outputChecked;
  const outputTotal = Number(outputEvidence?.data?.total) || 0;
  const outputUnavailableResponses = outputEntries.filter(({ status }) => status?.available !== true).length;
  const outputUnavailableAttempts = Math.max(0, outputAttempted - outputChecked);
  const outputComplete = (
    isAvailable(outputEvidence) &&
    outputChecked === outputTotal &&
    outputUnavailableResponses === 0
  );
  if (runningControllers > 0) {
    let outputDetail = "Output confirmation could not be loaded.";
    if (isAvailable(outputEvidence) && outputComplete) {
      outputDetail = `Output confirmation is available for all ${outputTotal} running application${outputTotal === 1 ? "" : "s"}.`;
    } else if (isAvailable(outputEvidence)) {
      const unavailable = outputUnavailableResponses + outputUnavailableAttempts;
      outputDetail = unavailable > 0
        ? `${unavailable} running application${unavailable === 1 ? " does" : "s do"} not currently report output confirmation.`
        : `Output confirmation was checked for ${outputChecked} of ${outputTotal} running applications.`;
    }
    coverage.push({
      key: "outputs",
      label: "Output confirmation",
      state: !isAvailable(outputEvidence) ? "Unavailable" : outputComplete ? "Available" : "Partial",
      detail: outputDetail,
    });
  }

  const protocolRows = [];
  for (const [key, label] of [["modbus", "Modbus TCP"], ["s7comm", "S7Comm"], ["opcua", "OPC UA"]]) {
    const source = evidence.protocols?.[key];
    if (!isAvailable(source)) {
      protocolRows.push({ key, label, state: "Unavailable" });
      continue;
    }
    const condition = protocolCondition(key, label, source.data);
    protocolRows.push({ key, label, state: condition.availability });
    if (condition.issue) issues.push(condition.issue);
  }
  const protocolSources = ["modbus", "s7comm", "opcua"].map((key) => evidence.protocols?.[key]);
  const protocolEvidenceComplete = protocolSources.every(isAvailable);
  const protocolEvidencePresent = protocolSources.some(isAvailable);
  const protocolStatusLabel = (state) => ({
    Disabled: "Off",
    Unavailable: "Not available",
    "Configuration only": "Setup only",
    "Evidence loaded": "Status loaded",
  }[state] || state);
  coverage.push({
    key: "protocols",
    label: "Data servers",
    state: protocolEvidenceComplete ? "Status loaded" : protocolEvidencePresent ? "Partial status" : "Unavailable",
    detail: protocolRows.map((item) => `${item.label}: ${protocolStatusLabel(item.state)}`).join(" · "),
  });

  const activeDeployments = deployments.filter((deployment) => (
    !["failed", "completed", "cancelled", "rolled_back", "rejected"].includes(String(deployment?.phase || "").toLowerCase())
  )).length;

  return {
    issues,
    coverage,
    technicalDetails: coverage.filter((item) => ["forces", "outputs", "protocols"].includes(item.key)),
    protocolRows,
    summary: {
      runtimeHosts: hostsAvailable ? { online: hosts.length - offlineHosts.length, total: hosts.length } : null,
      controllers: controllersAvailable ? { running: runningControllers, total: controllers.length } : null,
      connections: connectionSummary,
      deployments: deploymentsAvailable ? { active: activeDeployments, total: deployments.length } : null,
      knownIssues: issues.length,
    },
  };
}
